import { useUpdateIdentifiedSymptom } from '@/api/identified-symptom/identified-symptom.hooks';
import { useGetFoodSupplements } from '@/api/food-supplement/food-supplement.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { IdentifiedSymptom } from '@repo/types/src/identified-symptom';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const SYMPTOM_TYPES = [
  'CONSTIPATION',
  'HEADACHE',
  'LOW_ENERGY',
  'INSOMNIA',
  'SELF_CRITICAL',
  'BLOATING',
  'LOST',
  'GUILT',
  'CRAMPS',
  'POST_PARTUM',
  'MOOD_SWINGS',
  'ACNE',
  'VAGINAL_DRYNESS',
  'SAD',
  'ABDOMINAL_PAIN',
  'HEAVY_FLOW',
  'IRRITABILITY',
  'BREAST_TENDERNESS',
  'DIARRHEA',
  'FATIGUE',
  'BACK_PAIN',
  'LETHARGIC',
  'LOW_LIBIDO',
  'DEPRESSED',
  'OBSESSIVE_THOUGHTS',
  'IRREGULAR_FLOW',
  'VAGINAL_ITCHING',
  'IRREGULAR_CYCLE',
  'ANXIOUS',
  'NAUSEA',
  'CRAVINGS',
];

type FoodSupplementEntry = {
  foodSupplementId: number;
  orderNumber: number;
};

type EditIdentifiedSymptomProps = {
  item: IdentifiedSymptom;
  refetch: () => void;
};

export const EditIdentifiedSymptom = ({
  item,
  refetch,
}: EditIdentifiedSymptomProps) => {
  const mutation = useUpdateIdentifiedSymptom();
  const { foodSupplements } = useGetFoodSupplements({ take: 1000 });
  const [open, setOpen] = useState(false);

  const [references, setReferences] = useState(item.references);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>(
    item.symptoms.map((s) => s.symptom.type),
  );
  const [foodEntries, setFoodEntries] = useState<FoodSupplementEntry[]>(
    [...item.foodSupplements]
      .sort((a, b) => a.orderNumber - b.orderNumber)
      .map((fs) => ({
        foodSupplementId: fs.foodSupplementId,
        orderNumber: fs.orderNumber,
      })),
  );

  const resetToItem = () => {
    setReferences(item.references);
    setSelectedSymptoms(item.symptoms.map((s) => s.symptom.type));
    setFoodEntries(
      [...item.foodSupplements]
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map((fs) => ({
          foodSupplementId: fs.foodSupplementId,
          orderNumber: fs.orderNumber,
        })),
    );
  };

  const toggleSymptom = (type: string) => {
    setSelectedSymptoms((prev) =>
      prev.includes(type) ? prev.filter((s) => s !== type) : [...prev, type],
    );
  };

  const addFoodEntry = () => {
    if (foodSupplements.length === 0) return;
    setFoodEntries((prev) => [
      ...prev,
      { foodSupplementId: foodSupplements[0].id, orderNumber: prev.length + 1 },
    ]);
  };

  const updateFoodEntry = (
    index: number,
    field: keyof FoodSupplementEntry,
    value: number,
  ) => {
    setFoodEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const removeFoodEntry = (index: number) => {
    setFoodEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = () => {
    if (!references.trim()) {
      toast.error('Les références sont obligatoires');
      return;
    }
    if (selectedSymptoms.length === 0) {
      toast.error('Sélectionnez au moins un symptôme');
      return;
    }

    mutation.mutate(
      {
        id: item.id,
        payload: {
          references: references.trim(),
          symptomTypes: selectedSymptoms,
          foodSupplementEntries: foodEntries,
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
          refetch();
          toast.success('Symptôme identifié mis à jour avec succès', {
            duration: 3000,
          });
        },
        onError: () => {
          toast.error('Erreur lors de la mise à jour', { duration: 3000 });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) resetToItem();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Pencil size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">
            Modifier le symptôme identifié
          </DialogTitle>
          <DialogDescription className="text-center">
            Modifiez les symptômes et compléments alimentaires associés
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Références *
            </label>
            <Input
              placeholder="Ex: lien1;lien2;lien3"
              value={references}
              onChange={(e) => setReferences(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Symptômes *
            </label>
            <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto border rounded p-2">
              {SYMPTOM_TYPES.map((type) => (
                <label
                  key={type}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-1 py-0.5 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedSymptoms.includes(type)}
                    onChange={() => toggleSymptom(type)}
                  />
                  {type}
                </label>
              ))}
            </div>
            {selectedSymptoms.length > 0 && (
              <p className="text-xs text-zinc-500 mt-1">
                {selectedSymptoms.length} sélectionné(s)
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Compléments alimentaires
            </label>
            <div className="flex flex-col gap-2">
              {foodEntries.map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    className="flex-1 border rounded px-2 py-1.5 text-sm"
                    value={entry.foodSupplementId}
                    onChange={(e) =>
                      updateFoodEntry(
                        index,
                        'foodSupplementId',
                        Number(e.target.value),
                      )
                    }
                  >
                    {foodSupplements.map((fs) => (
                      <option key={fs.id} value={fs.id}>
                        {fs.name}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs text-zinc-500">Ordre</span>
                    <Input
                      type="number"
                      min={1}
                      className="w-16"
                      value={entry.orderNumber}
                      onChange={(e) =>
                        updateFoodEntry(
                          index,
                          'orderNumber',
                          Number(e.target.value),
                        )
                      }
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFoodEntry(index)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onClick={addFoodEntry}
                disabled={foodSupplements.length === 0}
              >
                + Ajouter un complément
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Annuler</Button>
          </DialogClose>
          <Button
            variant="default"
            onClick={handleUpdate}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Mise à jour...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
